import { TFunction } from "i18next";
import React from "react";
import z from "zod";

import { AnnouncementSubscriber } from "@admin/api/announcements";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { createColumnHelper } from "@tanstack/react-table";

const columnHelper = createColumnHelper<AnnouncementSubscriber>();

export const invalidEmailPreviewLimit = 5;

export function getAnnouncementsTableColumns(t: TFunction<["pages", "common"]>) {
    return [
        columnHelper.accessor("mail", {
            id: "mail",
            header: t("pages:adminAnnouncementsPage.table.columns.mail"),
            cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
            enableSorting: true,
        }),
        columnHelper.accessor("source", {
            id: "source",
            header: t("common:label.source"),
            cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
            enableSorting: true,
        }),
    ];
}

function isValidEmail(email: string): boolean {
    return z.email().safeParse(email.trim()).success;
}

export function parseEmailList(raw: string): string[] {
    const seen = new Set<string>();
    const result: string[] = [];

    for (const token of raw.split(/[\n,;]+/)) {
        const trimmed = token.trim();
        if (!trimmed) {
            continue;
        }

        const key = trimmed.toLowerCase();
        if (seen.has(key)) {
            continue;
        }

        seen.add(key);
        result.push(trimmed);
    }

    return result;
}

export function validateEmailList(emails: string[]): { valid: string[]; invalid: string[] } {
    const valid: string[] = [];
    const invalid: string[] = [];

    for (const email of emails) {
        if (isValidEmail(email)) {
            valid.push(email.trim());
        } else {
            invalid.push(email);
        }
    }

    return { valid, invalid };
}

function emailsToTextarea(emails: string[]): string {
    return emails.join("\n");
}

export function isEmailInTextarea(raw: string, email: string): boolean {
    const normalized = email.trim().toLowerCase();
    return parseEmailList(raw).some((item) => item.toLowerCase() === normalized);
}

export function toggleEmailInTextarea(raw: string, email: string): string {
    const emails = parseEmailList(raw);
    const normalized = email.trim().toLowerCase();
    const isSelected = emails.some((item) => item.toLowerCase() === normalized);

    if (isSelected) {
        return emailsToTextarea(emails.filter((item) => item.toLowerCase() !== normalized));
    }

    const trimmed = email.trim();
    if (!trimmed) {
        return raw;
    }

    return emailsToTextarea([...emails, trimmed]);
}

export function removeProcessedEmailsFromTextarea(raw: string, removed: string[]): string {
    const removeSet = new Set(removed.map((email) => email.toLowerCase()));
    const remaining = parseEmailList(raw).filter((email) => !removeSet.has(email.toLowerCase()));
    return emailsToTextarea(remaining);
}
